import React, { Fragment, ReactNode } from 'react';

import t from '../../../labels/labelling-utils';
import { DRIVERS_RANKING_RACE_STARTING_GRID, DriversRankingVariants } from '../../../typings/drivers-ranking';
import {
  RACE_STATUS_CANCELLED,
  RACE_STATUS_FIXTURE,
  RACE_STATUS_PLAYING,
  RACE_STATUS_POSTPONED,
  RACE_STATUS_SUSPENDED,
  RaceSessionStatus,
} from '../../../typings/race-session';
import { isPlaying, showBestLapUpdate, showlastGrandPrixUpdate } from '../../../utils/race-status';
import { dateFormat } from '../../../utils/times';
import BestLapComponent from '../../atoms/best-lap/best-lap';
import IconMotorDashboardCalendar from '../../atoms/icons/icon-motor-dashboard-calendar';
import ResultsTitle from '../../atoms/results-title/results-title';
import Spacer from '../../atoms/spacer/spacer';
import CourtesyMessageComponent from '../../organisms/courtesy-message/courtesy-message';
import { ITopDriversTable } from '../../organisms/top-drivers-ranking-table/top-drivers-ranking-table';
import { FontSize } from '../../../typings/design-tokens';

import { hasFastestLaps } from './drivers-standings-helpers';

export const UpdateComponent = ({
  rankingVariant,
  lastGrandPrix,
  status,
  standingsList,
}: {
  rankingVariant: DriversRankingVariants;
  lastGrandPrix: string | undefined;
  status: RaceSessionStatus | undefined;
  standingsList: ITopDriversTable[];
}) => {
  if (lastGrandPrix && showlastGrandPrixUpdate(rankingVariant)) {
    return (
      <Fragment>
        `${t('drivers.standing.last.update.results')}: ${lastGrandPrix}`
      </Fragment>
    );
  } else if (showBestLapUpdate(rankingVariant) && hasFastestLaps(standingsList)) {
    return (
      <BestLapComponent color="pink" colorSvg="pink">
        {t('drivers.standing.best.lap.result')}
      </BestLapComponent>
    );
  } else if (isPlaying(status)) {
    return (
      <BestLapComponent color="red-light" colorSvg="blue" positionSvg="right">
        {t('motorsports.session.status.live')}
      </BestLapComponent>
    );
  } else {
    return null;
  }
};

const getCourtesyMessageMap = () => {
  return {
    [RACE_STATUS_FIXTURE]: t('motorsports.errorMessage.fixture'),
    [RACE_STATUS_PLAYING]: t('motorsports.errorMessage.playing'),
    [RACE_STATUS_CANCELLED]: t('motorsports.errorMessage.cancelled'),
    [RACE_STATUS_POSTPONED]: t('motorsports.errorMessage.postponed'),
    [RACE_STATUS_SUSPENDED]: t('motorsports.errorMessage.suspended'),
  };
};

export const CourtesyMessage = ({
  sessionStatus,
  rankingVariant,
  date,
  fontSize,
  dataSpaced,
}: {
  sessionStatus: RaceSessionStatus;
  rankingVariant: DriversRankingVariants;
  date?: string;
  fontSize?: FontSize;
  dataSpaced?: boolean;
}): JSX.Element | null => {
  const dateFormattedDay = dateFormat({ date: date!, format: 'dddd D MMMM' });
  const dateFormattedHour = dateFormat({ date: date!, format: 'HH:mm' }); // h24 format!
  const courtesyMessageMap = getCourtesyMessageMap();
  const message = courtesyMessageMap[sessionStatus];
  const hours = t('motorsports.errorMessage.hours');
  const dateFormatted = `${dateFormattedDay} ${hours} ${dateFormattedHour}`;

  switch (sessionStatus) {
    case RACE_STATUS_FIXTURE:
      return rankingVariant === DRIVERS_RANKING_RACE_STARTING_GRID ? (
        <CourtesyMessageComponent
          text={t('motorsports.errorMessage.fixtureGriglia')}
          backgroundIcon={<IconMotorDashboardCalendar size="xxlarge" color="medium-light" />}
          date={dateFormatted}
          fontSize={fontSize}
          dataSpaced={dataSpaced}
        />
      ) : (
        <CourtesyMessageComponent text={message} date={dateFormatted} fontSize={fontSize} dataSpaced={dataSpaced} />
      );

    case RACE_STATUS_PLAYING:
    case RACE_STATUS_CANCELLED:
    case RACE_STATUS_POSTPONED:
    case RACE_STATUS_SUSPENDED:
      return <CourtesyMessageComponent text={message} fontSize={fontSize} dataSpaced={dataSpaced} />;

    default:
      // RACE_STATUS_PLAYED
      return null;
  }
};

/**
 * Get result title element
 * @param grandPrixTitle title of undefined
 * @returns ResultsTitle JSX Element
 */
export const GrandPrixTitle = ({ children }: { children: ReactNode }) =>
  children && typeof children === 'string' ? (
    <Fragment>
      <ResultsTitle title={children} titleFontSize="18" />
      <Spacer size={20} />
    </Fragment>
  ) : null;
